import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { CheckCircle, Loader } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId && user) {
      pollPaymentStatus();
    } else if (!user) {
      navigate('/login');
    } else {
      navigate('/subscription');
    }
  }, [sessionId, user]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/status/${sessionId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.status === 'paid') {
          setStatus('success');
          await checkAuth();
          toast.success('Payment successful! Welcome to Premium!');
          return;
        }
        
        if (attempts < maxAttempts) {
          setAttempts(attempts + 1);
          setTimeout(pollPaymentStatus, 2000);
        } else {
          setStatus('timeout');
        }
      }
    } catch (error) {
      console.error('Payment check error:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      <main className="flex-1 py-16 px-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          {status === 'checking' && (
            <>
              <Loader className="w-16 h-16 text-primary mx-auto mb-4 animate-spin" />
              <h1 className="font-heading text-2xl font-bold text-babyblue-900 mb-2">
                Processing Payment
              </h1>
              <p className="text-babyblue-600">
                Please wait while we confirm your payment...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h1 className="font-heading text-2xl font-bold text-babyblue-900 mb-2">
                Payment Successful!
              </h1>
              <p className="text-babyblue-600 mb-6">
                Welcome to Germanatic Premium! You now have full access to all features.
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="btn-primary"
              >
                Go to Dashboard
              </button>
            </>
          )}

          {status === 'timeout' && (
            <>
              <h1 className="font-heading text-2xl font-bold text-babyblue-900 mb-2">
                Payment Processing
              </h1>
              <p className="text-babyblue-600 mb-6">
                Your payment is being processed. Check your email for confirmation.
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="btn-primary"
              >
                Go to Dashboard
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <h1 className="font-heading text-2xl font-bold text-babyblue-900 mb-2">
                Payment Error
              </h1>
              <p className="text-babyblue-600 mb-6">
                There was an error processing your payment. Please contact support.
              </p>
              <button
                onClick={() => navigate('/subscription')}
                className="btn-primary"
              >
                Try Again
              </button>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SubscriptionSuccessPage;
